import React, { useEffect } from 'react';
import useDownloadListFile from 'Hooks/query-hooks/list/useDownloadListFile';
import { useListFilesDownloadStore } from 'Hooks/zustand/useListFilesDownloadStore';
import { useTranslation } from 'react-i18next';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';

const useGetListFilePartsAndDownload = (id: string) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const fileDownloadingStore = useListFilesDownloadStore();
    // filter will always have only one object with this id
    const currentFileDownload = fileDownloadingStore
        .getFilesDownloading()
        .filter((downloadDataObject) => downloadDataObject.id === id)[0];

    // Mutations
    const downloadListFileMutation = useDownloadListFile({ id });

    const downloadFileParts = async (fileName: string, partNum: number) => {
        await downloadListFileMutation.mutateAsync({
            fileName,
            processType: currentFileDownload.processType,
            isInitiateDownload: false,
            partNum: partNum + 1
        });
    };

    // Functions
    /** Get hash string for given fileData Array with type Uint8Array */
    const calculateFileHash = async (data: Uint8Array): Promise<string> => {
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hash = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
        return hash;
    };

    const startLocalFileDownload = (fileName: string, blob: Blob) => {
        // create download link & click to start browser-based download that stores file locally
        const downloadUrl = URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = fileName;
        downloadLink.style.display = 'none';

        document.body.appendChild(downloadLink);
        downloadLink.click();

        // handle cleanup of links & state after download is completed
        document.body.removeChild(downloadLink);
        fileDownloadingStore.removeFileFromDownloading(id);
    };

    const processFilePartsToFile = async (fileName: string) => {
        const { fileParts, totalParts } = currentFileDownload;

        // combine all file parts into single Uint8Array only after all parts are downloaded.
        if (fileParts.length === totalParts) {
            const concatenatedArray = new Uint8Array(
                fileParts.reduce((totalLength, part) => totalLength + part.length, 0)
            );

            let offset = 0;

            fileParts.forEach((part) => {
                concatenatedArray.set(part, offset);
                offset += part.length;
            });

            const fileHash = await calculateFileHash(concatenatedArray);
            if (fileHash === currentFileDownload.fileIntegrityHash) {
                // convert UInt8Array to blob if data is not corrupted
                const blob = new Blob([concatenatedArray]);
                // stores file to local storage
                startLocalFileDownload(fileName, blob);
            } else {
                // fail because file data was corrupted
                fileDownloadingStore.removeFileFromDownloading(id);
                addAlert({
                    id: 'download-list-files-error',
                    status: 'danger',
                    content: t('File failed to download due to a network error')
                });
            }
        }
    };

    const initiateListFileDownload = React.useCallback(
        async (fileName: string, processType: string) => {
            // start download file parts from API process
            fileDownloadingStore.updateFileDownloadState({
                ...currentFileDownload,
                currentFileName: fileName,
                processType,
                id
            });
            await downloadListFileMutation.mutateAsync({ fileName, processType, isInitiateDownload: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        const { currentFileName, partCompleted, totalParts } = currentFileDownload;
        if (!currentFileName) return;

        if (partCompleted === 0) {
            // initiate the API download parts process
            downloadFileParts(currentFileName, 0);
        } else if (partCompleted && totalParts && partCompleted > 0 && partCompleted < totalParts) {
            // download next part from API
            downloadFileParts(currentFileName, partCompleted);
        } else if (partCompleted && partCompleted === totalParts) {
            // once all parts are downloaded, process them & store locally(complete download)
            processFilePartsToFile(currentFileName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFileDownload.totalParts, currentFileDownload.fileParts, currentFileDownload.partCompleted]);

    return {
        initiateListFileDownload,
        isLoading: downloadListFileMutation.isLoading
    };
};

export default useGetListFilePartsAndDownload;
